import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { useAuth } from '@/lib/auth/use-auth'
import { useOrganization } from '@/lib/org/use-organization'
import { AuthSplash } from '@/features/auth/LoginPage'
import OnboardingPage from '@/features/onboarding/OnboardingPage'

/**
 * Gate for every authenticated route.
 *
 * Order matters: wait for the session to settle, then require a session, then
 * wait for organizations, then require one. Checking in any other order flashes
 * the login screen on a hard refresh while the persisted token is still being
 * read back from storage.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth()
  const location = useLocation()

  if (authLoading) return <AuthSplash />

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return <OrganizationGate>{children}</OrganizationGate>
}

function OrganizationGate({ children }: { children: ReactNode }) {
  const { organization, loading, error, needsOnboarding } = useOrganization()

  if (loading) return <AuthSplash message="Loading your workspace…" />

  if (error) {
    return (
      <ErrorPanel
        title="Could not load your workspace"
        detail={error}
        hint="If this persists, the database migrations may not have been applied yet."
      />
    )
  }

  // A signed-in user with no organization gets the first-run setup, not a
  // dead-end — one form call seeds a complete set of books.
  if (needsOnboarding || !organization) {
    return <OnboardingPage />
  }

  return <>{children}</>
}

function ErrorPanel({
  title,
  detail,
  hint,
}: {
  title: string
  detail: string
  hint?: string | undefined
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-4">
      <div className="max-w-md rounded-card border border-border bg-surface p-6 text-center">
        <h1 className="text-base font-semibold text-fg">{title}</h1>
        <p className="mt-2 text-sm text-fg-secondary">{detail}</p>
        {hint && <p className="mt-3 text-xs text-fg-muted">{hint}</p>}
      </div>
    </div>
  )
}
